import React, { type Ref } from 'react';

import { defineMessage, IntlProvider, useIntl } from 'react-intl';

import Button from '@atlaskit/button/default/button';
import { Box } from '@atlaskit/primitives/compiled/box';
import { ModalContextProvider } from '@atlassian/entry-points/modal-context-provider';
import { ModalTrigger } from '@atlassian/entry-points/modal-trigger';

import { errorModalEntryPoint } from '../fixtures/legacy/errorModal.entrypoint';
import { RelayWrapper } from '../utils/wrapper';

const triggerLabel = defineMessage({
	id: 'entry-points.examples.modal.error.trigger.ai-non-final',
	defaultMessage: 'Open unavailable modal',
	description: 'Label on the example button that opens a modal whose content fails to load.',
});
const title = defineMessage({
	id: 'entry-points.examples.modal.error.title.ai-non-final',
	defaultMessage: 'Could not load issue',
	description: 'Title shown in the modal error dialog after the example entry point fails to load.',
});
const errorText = defineMessage({
	id: 'entry-points.examples.modal.error.text.ai-non-final',
	defaultMessage: 'The issue content is unavailable. Close this dialog and try again.',
	description:
		'Error message shown in the modal error dialog after the example entry point fails to load.',
});
const errorCloseText = defineMessage({
	id: 'entry-points.examples.modal.error.close.ai-non-final',
	defaultMessage: 'Close',
	description:
		'Label for the button that closes the modal error dialog after the example entry point fails to load.',
});

const frenchMessages = {
	[triggerLabel.id]: 'Ouvrir la fenêtre indisponible',
	[title.id]: 'Impossible de charger le ticket',
	[errorText.id]: 'Le contenu du ticket est indisponible. Fermez cette fenêtre et réessayez.',
	[errorCloseText.id]: 'Fermer le message d’erreur',
};

function TranslatedErrorModal(): React.JSX.Element {
	const { formatMessage } = useIntl();

	return (
		<ModalTrigger
			entryPoint={errorModalEntryPoint}
			title={formatMessage(title)}
			errorText={formatMessage(errorText)}
			errorCloseText={formatMessage(errorCloseText)}
		>
			{({ ref }) => (
				<Button ref={ref as Ref<HTMLButtonElement>}>{formatMessage(triggerLabel)}</Button>
			)}
		</ModalTrigger>
	);
}

export default function ModalTriggerErrorInternationalization(): React.JSX.Element {
	return (
		<IntlProvider locale="fr" defaultLocale="en" messages={frenchMessages}>
			<RelayWrapper>
				<Box lang="fr">
					<ModalContextProvider>
						<TranslatedErrorModal />
					</ModalContextProvider>
				</Box>
			</RelayWrapper>
		</IntlProvider>
	);
}
